package top.wcpe.allincore.dsl.compiler.theme

/**
 * 主题派生：由少量种子 token（seed.*）+ algorithm 自动补全整套梯度色、文本层级与几何 token。
 *
 * 设计要点：
 * - **present-wins**：只填缺失的 key（[put] 用 containsKey 守卫），主题文件已显式写出的 token
 *   一律不覆盖。因此现有 9 个完整主题（无 seed）不受影响、字节不变、像素零漂移。
 * - **双轨产出**：既回填 MD3 既有 token 名（primary/onPrimary/surface/outline…，让 8 个交互控件
 *   Renderer 零改即可吃到派生色），又新增 Ant 别名 token（colorPrimary/Hover/Bg、colorText* 等）
 *   供 DSL 显式引用。
 * - 颜色梯度由 [ColorMath.generate] 按 @ant-design/colors 算法生成；文本/中性层级由 alpha 合成。
 *
 * 纯函数：输入展平后的 token 表，输出补全后的新表，不改入参。
 */
object ThemeDerivation {

    /** 是否含可派生的种子。无种子时上层应跳过派生。 */
    fun hasSeed(tokens: Map<String, Any>): Boolean = tokens.keys.any { it.startsWith("seed.") }

    /**
     * 由种子派生补全 token。无 `seed.colorPrimary` 时原样返回（不派生）。
     */
    fun derive(tokens: LinkedHashMap<String, Any>): LinkedHashMap<String, Any> {
        val out = LinkedHashMap(tokens)
        val primary = str(tokens["seed.colorPrimary"]) ?: return out
        val dark = (str(tokens["algorithm"]) ?: "dark").equals("dark", ignoreCase = true)

        val success = str(tokens["seed.colorSuccess"]) ?: "#FF52C41A"
        val warning = str(tokens["seed.colorWarning"]) ?: "#FFFAAD14"
        val error = str(tokens["seed.colorError"]) ?: "#FFFF4D4F"
        val info = str(tokens["seed.colorInfo"]) ?: primary
        val textBase = str(tokens["seed.colorTextBase"]) ?: if (dark) "#FFFFFFFF" else "#FF000000"
        val bgBase = str(tokens["seed.colorBgBase"]) ?: if (dark) "#FF1A1A1A" else "#FFFFFFFF"
        val borderRadius = num(tokens["seed.borderRadius"]) ?: 6.0
        val sizeUnit = num(tokens["seed.sizeUnit"]) ?: 4.0
        val controlHeight = num(tokens["seed.controlHeight"]) ?: 16.0

        // —— 主色族（MD3 primary 回填 + Ant colorPrimary 别名）——
        antPalette(out, "colorPrimary", primary)
        md3ColorFamily(out, "primary", "Primary", primary, dark)
        // 次色暂同主色族（缺 secondary 种子时由主色顶替，保证 MD3 token 完整）
        md3ColorFamily(out, "secondary", "Secondary", primary, dark)

        // —— 语义色族 ——
        antPalette(out, "colorSuccess", success)
        antPalette(out, "colorWarning", warning)
        antPalette(out, "colorError", error)
        antPalette(out, "colorInfo", info)
        put(out, "successColor", colorAt(success, 5))
        put(out, "onSuccess", ColorMath.contrastText(colorAt(success, 5)))
        put(out, "warningColor", colorAt(warning, 5))
        put(out, "onWarning", ColorMath.contrastText(colorAt(warning, 5)))
        put(out, "error", colorAt(error, 5))
        put(out, "onError", ColorMath.contrastText(colorAt(error, 5)))
        put(out, "errorContainer", if (dark) colorAt(error, 7) else colorAt(error, 0))
        put(out, "onErrorContainer", if (dark) colorAt(error, 1) else colorAt(error, 7))
        put(out, "infoColor", colorAt(info, 5))
        put(out, "onInfo", ColorMath.contrastText(colorAt(info, 5)))

        // —— 四级文本（对齐 Ant：88/65/45/25%）+ MD3 回填 ——
        val text = ColorMath.alphaBlend(bgBase, textBase, 0.88)
        val textSecondary = ColorMath.alphaBlend(bgBase, textBase, 0.65)
        val textTertiary = ColorMath.alphaBlend(bgBase, textBase, 0.45)
        val textQuaternary = ColorMath.alphaBlend(bgBase, textBase, 0.25)
        put(out, "colorText", text)
        put(out, "colorTextSecondary", textSecondary)
        put(out, "colorTextTertiary", textTertiary)
        put(out, "colorTextQuaternary", textQuaternary)
        put(out, "onBackground", text)
        put(out, "onSurface", text)
        put(out, "onSurfaceVariant", textSecondary)

        // —— 中性填充（对齐 Ant：15/6/4/2%）——
        put(out, "colorFill", ColorMath.alphaBlend(bgBase, textBase, 0.15))
        put(out, "colorFillSecondary", ColorMath.alphaBlend(bgBase, textBase, 0.06))
        put(out, "colorFillTertiary", ColorMath.alphaBlend(bgBase, textBase, 0.04))
        put(out, "colorFillQuaternary", ColorMath.alphaBlend(bgBase, textBase, 0.02))

        // —— 背景 / 表面（MD3 surface 阶 + Ant colorBgContainer）——
        put(out, "background", bgBase)
        put(out, "surface", bgBase)
        put(out, "colorBgContainer", bgBase)
        put(out, "surfaceContainerLowest", bgBase)
        put(out, "surfaceContainerLow", ColorMath.alphaBlend(bgBase, textBase, 0.02))
        put(out, "surfaceContainer", ColorMath.alphaBlend(bgBase, textBase, 0.04))
        put(out, "surfaceContainerHigh", ColorMath.alphaBlend(bgBase, textBase, 0.06))
        put(out, "surfaceContainerHighest", ColorMath.alphaBlend(bgBase, textBase, 0.08))

        // —— 边框（MD3 outline + Ant colorBorder）——
        val border = ColorMath.alphaBlend(bgBase, textBase, 0.15)
        val borderSecondary = ColorMath.alphaBlend(bgBase, textBase, 0.06)
        put(out, "colorBorder", border)
        put(out, "colorBorderSecondary", borderSecondary)
        put(out, "outline", border)
        put(out, "outlineVariant", borderSecondary)

        // —— 几何：圆角档位 / 间距梯度 / 控件高度 ——
        put(out, "cornerRadiusXSmall", (borderRadius - 4).coerceAtLeast(0.0))
        put(out, "cornerRadiusSmall", (borderRadius - 2).coerceAtLeast(0.0))
        put(out, "cornerRadiusMedium", borderRadius)
        put(out, "cornerRadiusLarge", borderRadius + 2)
        put(out, "cornerRadiusExtraLarge", borderRadius * 2)
        put(out, "cornerRadiusFull", 999.0)
        put(out, "radiusSm", (borderRadius - 2).coerceAtLeast(0.0))
        put(out, "radiusMd", borderRadius)
        put(out, "radiusLg", borderRadius + 2)
        put(out, "spaceXs", sizeUnit * 0.5)
        put(out, "spaceSm", sizeUnit)
        put(out, "spaceMd", sizeUnit * 2)
        put(out, "spaceLg", sizeUnit * 3)
        put(out, "spaceXl", sizeUnit * 4)
        put(out, "controlHeightSM", controlHeight - 4)
        put(out, "controlHeightMD", controlHeight)
        put(out, "controlHeightLG", controlHeight + 6)
        put(out, "borderWidth", 1.0)

        // —— Motion token（动效设计系统：duration 梯度 + easing 曲线 + 出入场/编排时长）——
        // 对齐 Ant motionDuration Fast/Mid/Slow 与 MD3 short/medium/long；easing 为曲线名字符串，
        // 由运行时映射到 Easing 实现（emphasized 为 MD3 强调曲线，runtime 待补 Easing 支持）。
        put(out, "motionDurationFast", 100.0)
        put(out, "motionDurationMid", 200.0)
        put(out, "motionDurationSlow", 300.0)
        put(out, "motionDurationSlower", 500.0)
        put(out, "motionEaseStandard", "easeInOut")
        put(out, "motionEaseEmphasized", "emphasized")
        put(out, "motionEaseOut", "easeOut")
        put(out, "motionEaseIn", "easeIn")
        put(out, "motionEaseInOut", "easeInOut")
        put(out, "motionLinear", "linear")
        // 出入场 / 成组编排时长（泛化原 Arwes 专属 animDuration*，所有 seed 主题通用）
        put(out, "animDurationEnterMs", 200.0)
        put(out, "animDurationExitMs", 150.0)
        put(out, "animDurationStaggerMs", 40.0)

        return out
    }

    /** Ant 风格别名：colorX / Hover / Active / Bg / BgHover / Border / BorderHover + colorX-1..10。 */
    private fun antPalette(out: LinkedHashMap<String, Any>, base: String, seed: String) {
        val p = ColorMath.generate(seed)
        put(out, base, p[5])
        put(out, "${base}Hover", p[4])
        put(out, "${base}Active", p[6])
        put(out, "${base}Bg", p[0])
        put(out, "${base}BgHover", p[1])
        put(out, "${base}Border", p[2])
        put(out, "${base}BorderHover", p[3])
        for (i in 0..9) put(out, "$base-${i + 1}", p[i])
    }

    /** MD3 色族回填：base / onBase / baseContainer / onBaseContainer。 */
    private fun md3ColorFamily(
        out: LinkedHashMap<String, Any>,
        base: String,
        cap: String,
        seed: String,
        dark: Boolean,
    ) {
        val p = ColorMath.generate(seed)
        put(out, base, p[5])
        put(out, "on$cap", ColorMath.contrastText(p[5]))
        put(out, "${base}Container", if (dark) p[7] else p[0])
        put(out, "on${cap}Container", if (dark) p[1] else p[7])
    }

    /** 取某种子色 generate 后第 idx 级（0..9）。 */
    private fun colorAt(seed: String, idx: Int): String = ColorMath.generate(seed)[idx]

    /** present-wins：仅当不存在时写入。 */
    private fun put(out: LinkedHashMap<String, Any>, key: String, value: Any) {
        if (!out.containsKey(key)) out[key] = value
    }

    private fun str(v: Any?): String? = v as? String

    private fun num(v: Any?): Double? = when (v) {
        is Double -> v
        is Int -> v.toDouble()
        is Float -> v.toDouble()
        is Long -> v.toDouble()
        is String -> v.toDoubleOrNull()
        else -> null
    }
}
